import { Checkmark12Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { cn } from '@web/shared/util/cn';

/**
 * Amostra de cor — a cor que QUEM USA escolheu para uma categoria (o tipo de uma atividade). É a
 * paleta categórica do tema (`--swatch-*` em `styles.css`): nove tons médios que levam texto
 * branco por cima, iguais nos dois temas. Nunca hex na tela: a tela guarda o NOME da cor.
 *
 *   ColorSwatch        o ponto de 10 (ou 16 com `size="md"`), redondo
 *   ColorSwatchPicker  um grupo de rádio com as nove amostras de 24; a marcada ganha o ✓ e
 *                      o anel de foco; setas mudam a escolha (o grupo é UM ponto de Tab)
 *
 * `swatchBackground(cor)` dá a classe de fundo para quem pinta uma superfície maior (a barra
 * de uma coluna do kanban). A cor por POSIÇÃO numa sequência (as etapas de um funil) não é
 * escolhida: é a rampa da marca (`brand-ramp.tsx`).
 */

export const SWATCH_COLORS = [
  'gray',
  'blue',
  'teal',
  'green',
  'marigold',
  'orange',
  'red',
  'purple',
  'berry',
] as const;

export type SwatchColor = (typeof SWATCH_COLORS)[number];

// Classes inteiras, escritas aqui: o Tailwind só gera o que encontra literalmente no código.
const BACKGROUND: Record<SwatchColor, string> = {
  gray: 'bg-swatch-gray',
  blue: 'bg-swatch-blue',
  teal: 'bg-swatch-teal',
  green: 'bg-swatch-green',
  marigold: 'bg-swatch-marigold',
  orange: 'bg-swatch-orange',
  red: 'bg-swatch-red',
  purple: 'bg-swatch-purple',
  berry: 'bg-swatch-berry',
};

/** A classe de fundo da cor. */
export function swatchBackground(color: SwatchColor): string {
  return BACKGROUND[color];
}

const TEXT: Record<SwatchColor, string> = {
  gray: 'text-swatch-gray',
  blue: 'text-swatch-blue',
  teal: 'text-swatch-teal',
  green: 'text-swatch-green',
  marigold: 'text-swatch-marigold',
  orange: 'text-swatch-orange',
  red: 'text-swatch-red',
  purple: 'text-swatch-purple',
  berry: 'text-swatch-berry',
};

/** A cor no texto ou no ícone (o ícone de um tipo de atividade). */
export function swatchText(color: SwatchColor): string {
  return TEXT[color];
}

// The tint grows on hover; the alpha is the same in both themes (the swatches are mid tones).
const TINT: Record<SwatchColor, string> = {
  gray: 'bg-swatch-gray/12 hover:bg-swatch-gray/20',
  blue: 'bg-swatch-blue/12 hover:bg-swatch-blue/20',
  teal: 'bg-swatch-teal/12 hover:bg-swatch-teal/20',
  green: 'bg-swatch-green/12 hover:bg-swatch-green/20',
  marigold: 'bg-swatch-marigold/12 hover:bg-swatch-marigold/20',
  orange: 'bg-swatch-orange/12 hover:bg-swatch-orange/20',
  red: 'bg-swatch-red/12 hover:bg-swatch-red/20',
  purple: 'bg-swatch-purple/12 hover:bg-swatch-purple/20',
  berry: 'bg-swatch-berry/12 hover:bg-swatch-berry/20',
};

/**
 * Um bloco marcado pela cor: o fundo num tom claro da cor, um pouco mais forte no hover (o
 * evento de uma agenda). O fundo é translúcido: sobre uma grade, ponha-o numa camada acima de
 * um fundo opaco (`bg-surface`) para as linhas não aparecerem através dele.
 */
export function swatchTint(color: SwatchColor): string {
  return TINT[color];
}

function ColorSwatch({
  color,
  size = 'sm',
  className,
  ...props
}: React.ComponentProps<'span'> & { color: SwatchColor; size?: 'sm' | 'md' }) {
  return (
    <span
      data-slot="color-swatch"
      aria-hidden
      className={cn(
        'inline-block shrink-0 rounded-full',
        size === 'sm' ? 'size-2.5' : 'size-4',
        BACKGROUND[color],
        className,
      )}
      {...props}
    />
  );
}

function ColorSwatchPicker({
  value,
  onChange,
  labels,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  ...props
}: Omit<React.ComponentProps<'div'>, 'onChange'> & {
  value: SwatchColor;
  onChange: (color: SwatchColor) => void;
  /** O nome de cada cor, para o leitor de tela e a dica ("Azul"). */
  labels: Record<SwatchColor, string>;
  disabled?: boolean;
  'aria-label': string;
}) {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const select = (index: number) => {
    const color = SWATCH_COLORS[(index + SWATCH_COLORS.length) % SWATCH_COLORS.length];
    if (!color) return;
    onChange(color);
    refs.current[SWATCH_COLORS.indexOf(color)]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-slot="color-swatch-picker"
      className={cn('flex flex-wrap items-center gap-1.5', className)}
      {...props}
    >
      {SWATCH_COLORS.map((color, index) => {
        const checked = color === value;
        return (
          <button
            key={color}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={labels[color]}
            title={labels[color]}
            disabled={disabled}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(color)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                select(index + 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                select(index - 1);
              }
            }}
            className={cn(
              'flex size-6 items-center justify-center rounded-full text-white transition-shadow duration-(--duration-fast)',
              'focus-visible:ring-brand focus-visible:ring-offset-surface outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              'disabled:opacity-50',
              checked && 'ring-foreground ring-offset-surface ring-2 ring-offset-2',
              BACKGROUND[color],
            )}
          >
            {checked && <Checkmark12Regular aria-hidden className="size-3" />}
          </button>
        );
      })}
    </div>
  );
}

export { ColorSwatch, ColorSwatchPicker };
